import { useAuth } from '@/app/providers';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Building2, Check, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export function GadSelector() {
  const { memberships, activeMembershipId, activeGad, switchGad, isSwitchingGad } = useAuth();

  // If only one GAD, show without dropdown
  if (memberships.length <= 1) {
    const singleGadName = activeGad?.name ?? 'Sin GAD';
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 px-3 py-2">
            <GadAvatar name={activeGad?.name} logo={activeGad?.logo} />
            <span className="hidden max-w-[150px] truncate text-sm font-medium sm:inline-block">{singleGadName}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[300px]">
          {singleGadName}
        </TooltipContent>
      </Tooltip>
    );
  }

  const handleSwitchGad = async (gadId: number) => {
    if (gadId === activeGad?.id) return;
    try {
      await switchGad(gadId);
    } catch {
      // Error is handled by the API client
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 px-3" disabled={isSwitchingGad}>
          {isSwitchingGad ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <GadAvatar name={activeGad?.name} logo={activeGad?.logo} />
          )}
          <span className="hidden max-w-[150px] truncate text-sm font-medium sm:inline-block">
            {activeGad?.name ?? 'Seleccionar GAD'}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[280px]">
        {memberships.map((membership) => {
          const isActive = membership.id === activeMembershipId;
          return (
            <DropdownMenuItem
              key={membership.id}
              onClick={() => void handleSwitchGad(membership.gad.id)}
              className={cn('flex cursor-pointer items-center gap-3 py-3', isActive && 'bg-accent')}
            >
              <GadAvatar name={membership.gad.name} logo={membership.gad.logo} size="sm" />
              <span className="flex-1 text-sm">{membership.gad.name}</span>
              {isActive && <Check className="h-4 w-4 shrink-0 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface GadAvatarProps {
  name?: string | null;
  logo?: string | null;
  size?: 'sm' | 'md';
}

function GadAvatar({ name, logo, size = 'md' }: GadAvatarProps) {
  const sizeClasses = size === 'sm' ? 'h-6 w-6' : 'h-8 w-8';

  return (
    <Avatar className={sizeClasses}>
      {logo && <AvatarImage src={logo} alt={name ?? 'GAD'} />}
      <AvatarFallback className="bg-primary/10 text-primary">
        <Building2 className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />
      </AvatarFallback>
    </Avatar>
  );
}
